// reviewable-kind-spec — ONE declaration for one reviewable kind.
//
// A reviewable kind is an artefact an agent emits and a human then approves or
// rejects. Before this module the facts about one kind were spread over eleven
// keyed tables in three packages: the wire enum, the artefact-type union, the
// web artefact-table map, the finding action config, the composition action
// config, the composer source list, the server finding-table map, the runner's
// reviewable-type set, and a per-kind card wrapper file with a switch over it.
// A new kind had to be added to every one of them, by hand, and nothing failed
// when it was not. Six task types were missing from one map for months.
//
// This table holds the PRESENTATION facts of a kind, and it holds them once.
// The consumers derive; they no longer re-declare.
//
// What this module deliberately does NOT hold (CONTEXT.md — "Client
// Application Architecture", ADR-0003):
//
//   - the Object Type, the personas, the Actions, the materialise hook and the
//     precheck. They are DOMAIN facts, they stay on the server, and they stay
//     in `apps/server/src/modules/agent/reviewable-registry.ts`.
//   - the persistence maps. `rowTable` below is a NAME the UI shows and keys a
//     cache by. It is never a query target: every read goes through `ctx.read`
//     (ADR-0002).
//
// The two layers are held together by an explicit parity guard that fails the
// build on drift: `apps/server/src/modules/agent/__tests__/
// reviewable-kind-parity.test.ts`.
//
// TO ADD A KIND you write two entries: one here, and one in the server
// registry. `packages/contract/src/reviewable-kind-spec.test.ts` proves that
// claim by walking a fictional kind through every consumer.

import { z } from 'zod';

import { AGENT_ROW_TABLES, type AgentRowTable } from './schemas/agent-rows';
import {
    callArtefactPayloadSchema,
    coacActionDraftPayloadSchema,
    monthlyNarrativeDraftPayloadSchema,
    relationshipProposalPayloadSchema,
} from './schemas/review-payload';
import type { AgentTaskType } from './agent-task-view';

/** The four staff frontends a review item can land on. `global` is not one of
 *  them: an item always belongs to exactly one persona's queue. */
export type ReviewFrontend = 'desk' | 'ops' | 'comply' | 'view';

/**
 * Which generic body renders the kind.
 *
 * - `finding` — the generic `<FindingCard>`: rule citation, evaluation table,
 *   alternatives, counterfactual, glass-box trace, decision bar.
 * - `workbench` — the generic `<CompositionWorkbench>`: brief, sources, plan,
 *   variants, redline, decision bar.
 * - `bespoke` — the kind writes its own body, because no generic frame fits
 *   (the staged call-drafts panel, the CA draft form, the narrative panel).
 *
 * A bespoke body exists ONLY where this field says `bespoke`. A per-kind
 * wrapper file over a generic frame is projection tax, and the two guards in
 * the test file refuse to let one come back.
 */
export type ReviewFrame = 'finding' | 'workbench' | 'bespoke';

/**
 * The grain of one review item.
 *
 * `per-row` gives one item for each artefact row. `{ by: 'callId' }` collapses
 * every row of one telephone call into a single item, so the IM clears the
 * call once instead of four times.
 */
export type ReviewGrouping = 'per-row' | { readonly by: 'callId' };

/**
 * Which dispatcher the decision buttons call.
 *
 * `reviewable` is the generic family, `/api/agent/reviewables/:kind/:id/…`.
 * `bespoke-endpoint` means the approve carries an effect the generic status
 * flip cannot express, so the kind keeps typed endpoints of its own (VIGIL
 * raises an alert, AEGIS-Breach writes the register, SCRIVENER materialises a
 * letter). The wire enum below is exactly the `reviewable` subset, so the two
 * can no longer disagree.
 */
export type ReviewableFamily = 'reviewable' | 'bespoke-endpoint';

/** The pending-payload arrays that hold composer draft rows. */
export type ComposerPayloadKey =
    | 'suitabilityLetterDrafts'
    | 'onboardingPackDrafts'
    | 'clientCommunicationDrafts'
    | 'fundReportDrafts'
    | 'vulnerabilityAdaptationPlanDrafts'
    | 'governancePaperDrafts'
    | 'fairValueAssessmentDrafts';

export interface ReviewableKindSpec {
    /** The physical row table. A NAME, never a query target (ADR-0002). */
    readonly rowTable: string;
    /** The staff frontend that reviews this kind. */
    readonly surface: ReviewFrontend;
    readonly grouping: ReviewGrouping;
    readonly frame: ReviewFrame;
    /** Human noun phrase for the queue row, e.g. "Onboarding pack". */
    readonly label: string;
    /** The agent job key. It is also the `listFindings` path parameter for a
     *  `finding` kind and the composer key for a `workbench` kind. */
    readonly agent: string;
    /** The eyebrow the card header shows, e.g. "AEGIS · KYC". */
    readonly agentLabel: string;
    readonly family: ReviewableFamily;
    /**
     * The AgentTask type that produces the kind. It binds this table to
     * `TASK_TYPE_FRONTEND`: the guard checks that `surface` is the frontend
     * that map already gives for this task type, so the two cannot disagree.
     */
    readonly taskType: AgentTaskType;
    /** The pending-payload array holding the rows. Composer kinds only. */
    readonly payloadKey?: ComposerPayloadKey;
    /**
     * The typed answers the reviewer stages BESIDE the thumb, as a schema
     * (`./schemas/review-payload.ts`).
     *
     * The thumb answers "is this true?". A kind that asks a SECOND question
     * declares the shape of its answer here, and four kinds do. The wire
     * decision union is DERIVED from this field, so a kind carries its own
     * payload and no other kind's: `assignTo` and `promoteTo` used to sit on
     * the shared decision schema, where twenty-five kinds declared two fields
     * that four kinds read.
     *
     * A PRESENTATION fact, and it stays inside the rule of this module: the
     * payload is what the reviewer typed or ticked, never what the server
     * writes. The Object Type, the patch and the gate stay on the server
     * (ADR-0003).
     */
    readonly payload?: z.ZodTypeAny;
}

/**
 * THE declaration. One entry for one reviewable kind, keyed by the artefact
 * type key the runner stamps on the AgentTask. That key is also the wire
 * `kind` on `/api/agent/reviewables/:kind`, which is why the two never need a
 * translation table between them.
 *
 * `satisfies` (not a type annotation) so `AgentArtefactType` can be READ OFF
 * the keys below. The 25-arm union that used to sit in `agent-task-view.ts`
 * is gone; adding a key here adds the union member.
 */
const KIND_SPEC_TABLE = {
    // -------- ATLAS — the post-call artefacts of one telephone call --------
    // Four kinds, one item: the reviewer clears a call, not a row.
    'call-extracted-fact': {
        rowTable: 'call_extracted_facts',
        surface: 'desk',
        grouping: { by: 'callId' },
        frame: 'bespoke',
        label: 'Extracted fact',
        agent: 'ATLAS',
        agentLabel: 'ATLAS',
        family: 'reviewable',
        taskType: 'post_call_processing',
        payload: callArtefactPayloadSchema,
    },
    'call-action-item': {
        rowTable: 'call_action_items',
        surface: 'desk',
        grouping: { by: 'callId' },
        frame: 'bespoke',
        label: 'Action item',
        agent: 'ATLAS',
        agentLabel: 'ATLAS',
        family: 'reviewable',
        taskType: 'post_call_processing',
        payload: callArtefactPayloadSchema,
    },
    'call-kb-note': {
        rowTable: 'call_kb_notes',
        surface: 'desk',
        grouping: { by: 'callId' },
        frame: 'bespoke',
        label: 'Knowledge note',
        agent: 'ATLAS',
        agentLabel: 'ATLAS',
        family: 'reviewable',
        taskType: 'post_call_processing',
        payload: callArtefactPayloadSchema,
    },
    // SEP-215. The Object Type key and the table name diverge on purpose: the
    // table keeps its old name, so the ATLAS reprocess and purge SQL needs no
    // data move.
    'relationship-proposal': {
        rowTable: 'client_connections',
        surface: 'desk',
        grouping: { by: 'callId' },
        frame: 'bespoke',
        label: 'Relationship proposal',
        agent: 'ATLAS',
        agentLabel: 'ATLAS',
        family: 'reviewable',
        taskType: 'post_call_processing',
        payload: relationshipProposalPayloadSchema,
    },
    'monthly-narrative-draft': {
        rowTable: 'wealth_monthly_narrative_drafts',
        surface: 'desk',
        grouping: 'per-row',
        frame: 'bespoke',
        label: 'Monthly narrative',
        agent: 'ATLAS',
        agentLabel: 'ATLAS',
        family: 'reviewable',
        taskType: 'monthly_narrative',
        payload: monthlyNarrativeDraftPayloadSchema,
    },

    // -------- Detector findings — the generic <FindingCard> --------
    'vigil-finding': {
        rowTable: 'wealth_vigil_findings',
        surface: 'comply',
        grouping: 'per-row',
        frame: 'finding',
        label: 'Mandate alert',
        agent: 'VIGIL',
        agentLabel: 'VIGIL',
        // Approve raises a suitability alert; reject supersedes siblings.
        family: 'bespoke-endpoint',
        taskType: 'suitability_check',
    },
    'onboarding-stall-finding': {
        rowTable: 'onboarding_stall_findings',
        surface: 'ops',
        grouping: 'per-row',
        frame: 'finding',
        label: 'Onboarding stall',
        agent: 'WARDEN',
        agentLabel: 'WARDEN',
        family: 'reviewable',
        taskType: 'onboarding_pipeline_watch',
    },
    'vulnerability-marker-finding': {
        rowTable: 'vulnerability_marker_findings',
        surface: 'comply',
        grouping: 'per-row',
        frame: 'finding',
        label: 'Vulnerability marker',
        agent: 'SENTINEL',
        agentLabel: 'SENTINEL',
        family: 'reviewable',
        taskType: 'vulnerability_flag',
    },
    'pcf-deviation-finding': {
        rowTable: 'pcf_deviation_findings',
        surface: 'desk',
        grouping: 'per-row',
        frame: 'finding',
        label: 'Framework deviation',
        agent: 'QUORUM',
        agentLabel: 'QUORUM',
        family: 'reviewable',
        taskType: 'framework_deviation',
    },
    'investment-trust-policy-finding': {
        rowTable: 'investment_trust_policy_findings',
        surface: 'view',
        grouping: 'per-row',
        frame: 'finding',
        label: 'Trust policy breach',
        agent: 'BEACON',
        agentLabel: 'BEACON',
        // Approve materialises a policy breach; reject uses the generic family.
        family: 'reviewable',
        taskType: 'fund_policy_watch',
    },
    'data-freshness-finding': {
        rowTable: 'data_freshness_findings',
        surface: 'comply',
        grouping: 'per-row',
        frame: 'finding',
        label: 'Stale data',
        agent: 'PULSE',
        agentLabel: 'PULSE',
        family: 'reviewable',
        taskType: 'data_freshness_check',
    },
    'reconciliation-classification-finding': {
        rowTable: 'reconciliation_classification_findings',
        surface: 'ops',
        grouping: 'per-row',
        frame: 'finding',
        label: 'Break classification',
        agent: 'MASON',
        agentLabel: 'MASON',
        family: 'reviewable',
        taskType: 'reconciliation_classification_batch',
    },
    'kyc-freshness-finding': {
        rowTable: 'kyc_freshness_findings',
        surface: 'comply',
        grouping: 'per-row',
        frame: 'finding',
        label: 'KYC freshness',
        agent: 'AEGIS_KYC',
        agentLabel: 'AEGIS · KYC',
        family: 'reviewable',
        taskType: 'kyc_renewal',
    },
    'sanctions-hit-finding': {
        rowTable: 'sanctions_hit_findings',
        surface: 'comply',
        grouping: 'per-row',
        frame: 'finding',
        label: 'Sanctions hit',
        agent: 'AEGIS_SANCTIONS',
        agentLabel: 'AEGIS · Sanctions',
        family: 'reviewable',
        taskType: 'sanctions_screening',
    },
    'breach-draft-finding': {
        rowTable: 'breach_draft_findings',
        surface: 'comply',
        grouping: 'per-row',
        frame: 'finding',
        label: 'Breach draft',
        agent: 'AEGIS_BREACH',
        agentLabel: 'AEGIS · Breach',
        // Approve writes the breach register; all three verbs are typed
        // endpoints on the comply-breach-register contract.
        family: 'bespoke-endpoint',
        taskType: 'breach_draft',
    },
    'cd-rescore-finding': {
        rowTable: 'cd_rescore_findings',
        surface: 'comply',
        grouping: 'per-row',
        frame: 'finding',
        label: 'Consumer-Duty rescore',
        agent: 'AEGIS_CD',
        agentLabel: 'AEGIS · Consumer Duty',
        family: 'reviewable',
        taskType: 'consumer_duty_rescore',
    },

    // -------- CEDAR — corporate actions --------
    'coac-action-draft': {
        rowTable: 'coac_action_drafts',
        surface: 'ops',
        grouping: 'per-row',
        frame: 'bespoke',
        label: 'Corporate action draft',
        agent: 'CEDAR',
        agentLabel: 'CEDAR',
        family: 'reviewable',
        taskType: 'coac_pdf_extraction',
        payload: coacActionDraftPayloadSchema,
    },
    // The plain-English breakdown is read by the IM on the Desk decisions
    // inbox, although CEDAR itself is an Ops agent.
    'coac-breakdown': {
        rowTable: 'coac_breakdowns',
        surface: 'desk',
        grouping: 'per-row',
        frame: 'bespoke',
        label: 'Corporate action breakdown',
        agent: 'CEDAR',
        agentLabel: 'CEDAR',
        family: 'reviewable',
        taskType: 'coac_breakdown',
    },

    // -------- SCRIVENER — the change summary of a refresh (SEP-351) --------
    // The IM reads it at the top of the new fact find. It is informational.
    'fact-find-change-summary': {
        rowTable: 'fact_find_change_summaries',
        surface: 'desk',
        grouping: 'per-row',
        frame: 'bespoke',
        label: 'Fact find change summary',
        agent: 'SCRIVENER',
        agentLabel: 'SCRIVENER',
        family: 'reviewable',
        taskType: 'fact_find_change_summary',
    },

    // -------- Composers — the generic <CompositionWorkbench> --------
    'suitability-letter-draft': {
        rowTable: 'suitability_letter_drafts',
        // SCRIVENER is an IM-facing composer: the Investment Manager reviews,
        // edits and approves the suitability draft.
        surface: 'desk',
        grouping: 'per-row',
        frame: 'workbench',
        label: 'Suitability letter',
        agent: 'SCRIVENER',
        agentLabel: 'SCRIVENER',
        // Approve materialises a SuitabilityLetter under comply-suitability.
        family: 'bespoke-endpoint',
        payloadKey: 'suitabilityLetterDrafts',
        taskType: 'suitability_letter_draft',
    },
    'onboarding-pack-draft': {
        rowTable: 'onboarding_pack_drafts',
        surface: 'desk',
        grouping: 'per-row',
        frame: 'workbench',
        label: 'Onboarding pack',
        agent: 'HERALD',
        agentLabel: 'HERALD',
        family: 'reviewable',
        payloadKey: 'onboardingPackDrafts',
        taskType: 'onboarding_pack_draft',
    },
    'client-communication-draft': {
        rowTable: 'client_communication_drafts',
        surface: 'desk',
        grouping: 'per-row',
        frame: 'workbench',
        label: 'Client communication',
        agent: 'EMISSARY',
        agentLabel: 'EMISSARY',
        family: 'reviewable',
        payloadKey: 'clientCommunicationDrafts',
        taskType: 'email_draft',
    },
    'fund-report-draft': {
        rowTable: 'fund_report_drafts',
        surface: 'view',
        grouping: 'per-row',
        frame: 'workbench',
        label: 'Fund report',
        agent: 'CHARTER',
        agentLabel: 'CHARTER',
        family: 'reviewable',
        payloadKey: 'fundReportDrafts',
        taskType: 'fund_rns_draft',
    },
    'vulnerability-adaptation-plan-draft': {
        rowTable: 'vulnerability_adaptation_plan_drafts',
        surface: 'comply',
        grouping: 'per-row',
        frame: 'workbench',
        label: 'Vulnerability adaptation plan',
        agent: 'HAVEN',
        agentLabel: 'HAVEN',
        family: 'reviewable',
        payloadKey: 'vulnerabilityAdaptationPlanDrafts',
        taskType: 'vulnerability_adaptation_plan',
    },
    'governance-paper-draft': {
        rowTable: 'governance_paper_drafts',
        surface: 'view',
        grouping: 'per-row',
        frame: 'workbench',
        label: 'Governance paper',
        agent: 'FORUM',
        agentLabel: 'FORUM',
        family: 'reviewable',
        payloadKey: 'governancePaperDrafts',
        taskType: 'governance_paper_draft',
    },
    'fair-value-assessment-draft': {
        rowTable: 'fair_value_assessment_drafts',
        surface: 'comply',
        grouping: 'per-row',
        frame: 'workbench',
        label: 'Fair-value assessment',
        agent: 'ARBITER',
        agentLabel: 'ARBITER',
        family: 'reviewable',
        payloadKey: 'fairValueAssessmentDrafts',
        taskType: 'fair_value_assessment',
    },
} as const satisfies Record<string, ReviewableKindSpec>;

/**
 * The declaration, widened to the interface. Read a spec through this: the
 * `as const` table above keeps the exact literal of each entry, which the type
 * derivations below need and which a plain reader does not.
 */
export const REVIEWABLE_KIND_SPECS: Readonly<Record<AgentArtefactType, ReviewableKindSpec>> =
    KIND_SPEC_TABLE;

/**
 * Every artefact-type key an agent can stamp on an AgentTask. READ OFF the
 * declaration above, so a new kind adds a union member and every exhaustive
 * `Record<AgentArtefactType, …>` in the two apps stops compiling until it is
 * handled.
 */
export type AgentArtefactType = keyof typeof KIND_SPEC_TABLE;

/** Exhaustive list of the artefact-type keys, in declaration order. */
export const ARTEFACT_TYPES = Object.keys(KIND_SPEC_TABLE) as AgentArtefactType[];

/**
 * Artefact types whose row carries the `Reviewable` capability, so a task that
 * produces one moves to `review_required` rather than `completed`. Every
 * declared kind is reviewable by definition — that is what the table declares
 * — so the runner reads this set instead of repeating the list.
 */
export const REVIEWABLE_ARTEFACT_TYPES: ReadonlySet<AgentArtefactType> = new Set(ARTEFACT_TYPES);

/**
 * The kinds the generic `/api/agent/reviewables/:kind/:id/…` family serves —
 * the whole table minus the kinds whose approve carries a bespoke effect.
 */
export type ReviewableKind = {
    [K in AgentArtefactType]: (typeof KIND_SPEC_TABLE)[K]['family'] extends 'reviewable'
        ? K
        : never;
}[AgentArtefactType];

// The runtime list of the same subset. The cast asserts non-emptiness, which
// `z.enum` requires and which the type-level filter cannot state; the test
// pins the membership against the type.
const REVIEWABLE_KIND_LIST = ARTEFACT_TYPES.filter(
    (k) => REVIEWABLE_KIND_SPECS[k].family === 'reviewable',
) as [ReviewableKind, ...ReviewableKind[]];

/** The wire enum. Derived, so it can no longer drift from the table. */
export const reviewableKindSchema = z.enum(REVIEWABLE_KIND_LIST);

// ---------------------------------------------------------------------------
// The decision payload, read off the declaration
// ---------------------------------------------------------------------------

/**
 * The payload schema a kind declares, or `never` when it declares none.
 *
 * `K extends AgentArtefactType ?` looks redundant and is not: it makes `K`
 * naked, so the conditional DISTRIBUTES over a union of kinds. Without it, a
 * union that mixes payload-bearing and plain kinds fails the check as a whole
 * and collapses to `never`.
 */
type PayloadSchemaOf<K extends AgentArtefactType> = K extends AgentArtefactType
    ? (typeof KIND_SPEC_TABLE)[K] extends { readonly payload: infer S }
        ? S
        : never
    : never;

/**
 * The typed answers a decision on this kind may carry, or `never` for a kind
 * that carries none.
 *
 * READ OFF the `payload` field of the declaration, so the wire union, the web
 * builder and the server patch all speak one type. A kind with no payload
 * gives `never`, and the decision union turns that into `payload?: never` — so
 * a payload on a plain kind is a compile error, and a mistyped or misnamed
 * field on the four payload-bearing kinds is one too.
 */
export type ReviewPayload<K extends AgentArtefactType> = [PayloadSchemaOf<K>] extends [never]
    ? never
    : PayloadSchemaOf<K> extends z.ZodType<infer T>
      ? T
      : never;

/** The kinds that ask a second question. Derived, never listed. */
export type KindWithPayload = {
    [K in AgentArtefactType]: (typeof KIND_SPEC_TABLE)[K] extends { readonly payload: z.ZodTypeAny }
        ? K
        : never;
}[AgentArtefactType];

/** The payload schema of a kind at run time, or null when it carries none. */
export function reviewPayloadSchema(type: string | null | undefined): z.ZodTypeAny | null {
    return reviewableKindSpec(type)?.payload ?? null;
}

/** Look one kind up. Returns null for an unknown or a typo'd key, so a stale
 *  enum on either side cannot silently bind to the wrong table. */
export function reviewableKindSpec(type: string | null | undefined): ReviewableKindSpec | null {
    if (!type) return null;
    if (!Object.prototype.hasOwnProperty.call(KIND_SPEC_TABLE, type)) return null;
    return REVIEWABLE_KIND_SPECS[type as AgentArtefactType];
}

/** The row table of a kind, or null when the key is unknown. */
export function artefactTableNameFor(type: string | null | undefined): string | null {
    return reviewableKindSpec(type)?.rowTable ?? null;
}

const AGENT_ROW_TABLE_SET: ReadonlySet<string> = new Set<string>(AGENT_ROW_TABLES);

/**
 * Like {@link artefactTableNameFor}, but resolves ONLY to the tables the raw
 * `/api/agent/rows` surface serves. Every finding table returns null, so a
 * finding task skips that fetch (which 400s for it) and falls back to its
 * trigger-detail line. The return type is the contract-owned `AgentRowTable`,
 * so a table dropped from the allow-list stops type-checking at the callers.
 */
export function agentRowTableFor(type: string | null | undefined): AgentRowTable | null {
    const table = artefactTableNameFor(type);
    if (table === null || !AGENT_ROW_TABLE_SET.has(table)) return null;
    return table as AgentRowTable;
}

/** The kinds one generic frame renders, at the type level. */
export type KindWithFrame<F extends ReviewFrame> = {
    [K in AgentArtefactType]: (typeof KIND_SPEC_TABLE)[K]['frame'] extends F ? K : never;
}[AgentArtefactType];

/** The kinds the generic `<FindingCard>` renders. */
export type FindingKind = KindWithFrame<'finding'>;
/** The kinds the generic `<CompositionWorkbench>` renders. */
export type WorkbenchKind = KindWithFrame<'workbench'>;

/** Every kind that the given generic frame renders. */
export function kindsWithFrame(frame: ReviewFrame): AgentArtefactType[] {
    return ARTEFACT_TYPES.filter((k) => REVIEWABLE_KIND_SPECS[k].frame === frame);
}

/**
 * The page a reviewer lands on from the drawer. DERIVED from the surface, not
 * stored: every kind's context route is its own frontend's agent board, and a
 * stored copy is one more table to keep in step.
 */
export function contextRouteFor(type: AgentArtefactType): string {
    return `/${REVIEWABLE_KIND_SPECS[type].surface}/agents`;
}

/** Detector agent key → its finding table. The `listFindings` path parameter
 *  is the agent key, so the server resolves the table through this map. */
export const FINDING_TABLE_BY_AGENT: ReadonlyMap<string, string> = new Map(
    kindsWithFrame('finding').map((k) => [
        REVIEWABLE_KIND_SPECS[k].agent,
        REVIEWABLE_KIND_SPECS[k].rowTable,
    ]),
);

/** Detector agent key → the finding kind it emits. The finding-frame agents
 * are one-kind-each, so this map is total over `DetectorAgent`. */
export const FINDING_KIND_BY_AGENT: ReadonlyMap<string, FindingKind> = new Map(
    kindsWithFrame('finding').map((k) => [REVIEWABLE_KIND_SPECS[k].agent, k as FindingKind]),
);

/** Composer agent key → the workbench kind it emits. The workbench-frame
 * agents are one-kind-each, so this map is total over `ComposerDraftAgent`. */
export const WORKBENCH_KIND_BY_AGENT: ReadonlyMap<string, WorkbenchKind> = new Map(
    kindsWithFrame('workbench').map((k) => [REVIEWABLE_KIND_SPECS[k].agent, k as WorkbenchKind]),
);

/** The agent keys `<DetectorFindingFeed agent="…">` accepts. */
export type DetectorAgent = (typeof KIND_SPEC_TABLE)[FindingKind]['agent'];

/** The composer agent keys the generic workbench renders. */
export type ComposerDraftAgent = (typeof KIND_SPEC_TABLE)[WorkbenchKind]['agent'];

// ATLAS emits five kinds and CEDAR two, so an agent key is unique only among
// the kinds a generic frame renders. Those are the only kinds this map answers
// for, and `kindForAgent` says so.
const KIND_BY_AGENT: ReadonlyMap<string, AgentArtefactType> = new Map(
    [...kindsWithFrame('finding'), ...kindsWithFrame('workbench')].map((k) => [
        REVIEWABLE_KIND_SPECS[k].agent,
        k,
    ]),
);

/** The kind a `finding` or `workbench` agent emits, or null for any other
 *  agent key. That is the one-agent-one-kind case the generic frames need. */
export function kindForAgent(agent: string): AgentArtefactType | null {
    return KIND_BY_AGENT.get(agent) ?? null;
}
